import { useState, KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { parseFlexibleInput, cn } from '@/utils'

interface AssetsInputWithTagsProps {
  value: string[]
  onChange: (assets: string[]) => void
  placeholder?: string
  label?: string
  quickOptions?: string[]
}

/**
 * Input component with tags/chips displayed inside the input field
 * Tags can be removed by clicking X button
 * New tags are added by typing and pressing Enter, comma, semicolon, space, or newline
 */
export const AssetsInputWithTags = ({
  value,
  onChange,
  placeholder = "e.g., Ethereum, Solana, Polygon (separate with commas, semicolons, or new lines)",
  label,
  quickOptions = []
}: AssetsInputWithTagsProps) => {
  const [inputValue, setInputValue] = useState('')

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ') {
      if (e.key === ' ' && !inputValue.trim()) return // Don't add if empty
      e.preventDefault()
      handleAddAssets()
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      onChange(value.slice(0, -1))
    }
  }

  const handleAddAssets = () => {
    if (!inputValue.trim()) return

    const parsedAssets = parseFlexibleInput(inputValue)
    if (parsedAssets.length === 0) return

    // Filter out duplicates
    const newAssets = parsedAssets.filter(
      asset => !value.some(existing => existing.toLowerCase() === asset.toLowerCase())
    )

    if (newAssets.length > 0) {
      onChange([...value, ...newAssets])
      setInputValue('')
    }
  }

  const handleRemoveAsset = (assetToRemove: string) => {
    onChange(value.filter(asset => asset !== assetToRemove))
  }

  const handleQuickOptionClick = (option: string) => {
    if (!value.some(existing => existing.toLowerCase() === option.toLowerCase())) {
      onChange([...value, option])
    }
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[#2d2923] dark:text-white leading-[20px] block">
          {label}
        </label>
      )}
      <div className="relative bg-white dark:bg-[#0d0d0d] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(255,255,255,0.12)] h-[48px] flex items-center gap-2 px-3 overflow-x-auto">
        {/* Tags */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {value.map((asset, index) => (
            <div
              key={index}
              className="bg-[#2d2923] dark:bg-white border border-[rgba(255,255,255,0.82)] dark:border-[rgba(45,41,35,0.82)] flex items-center gap-1 px-3 py-1.5 flex-shrink-0"
            >
              <span className="font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[13px] text-white dark:text-[#2d2923] leading-[18px] whitespace-nowrap">
                {asset}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveAsset(asset)}
                className="text-white dark:text-[#2d2923] hover:text-gray-300 dark:hover:text-[#2d2923] transition-colors flex-shrink-0"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        
        {/* Input */}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleAddAssets}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] bg-transparent border-0 outline-none font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] placeholder:text-[rgba(45,41,35,0.72)] dark:placeholder:text-[rgba(255,255,255,0.72)] leading-[20px]"
        />
      </div>

      {/* Quick Options */}
      {quickOptions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {quickOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleQuickOptionClick(option)}
              disabled={value.some(existing => existing.toLowerCase() === option.toLowerCase())}
              className={cn(
                "px-3 py-1.5 rounded-full font-['Plus_Jakarta_Sans',sans-serif] font-medium text-[12px] leading-[16px] transition-all border",
                value.some(existing => existing.toLowerCase() === option.toLowerCase())
                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-[rgba(28,28,28,0.6)] dark:text-[rgba(255,255,255,0.48)] dark:border-[rgba(255,255,255,0.12)]"
                  : "bg-white dark:bg-[rgba(28,28,28,0.32)] text-[#2d2923] dark:text-white border-[rgba(45,41,35,0.12)] dark:border-[rgba(255,255,255,0.12)] hover:border-[#2d2923] dark:hover:border-[rgba(255,255,255,0.2)] hover:bg-gray-50 dark:hover:bg-[rgba(28,28,28,0.48)]"
              )}
            >
              + {option}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Add cn helper import if not present, but it seems AssetsInputWithTags doesn't have it.
// Wait, I need to check if I need to import cn.

